 // src/components/Editor.tsx - CORRECTED VERSION
import { useState, useCallback, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Save, X, Eye, Code, Zap, Loader, AlertCircle, Check, Image, Upload, Trash2, Send } from 'lucide-react'
import TemplateSelector, { TEMPLATES } from './TemplateSelector'
import AIAnalyzer from './AIAnalyzer'
import { useAuth } from '../contexts/AuthContext'
import { getUserPlanLimits, canUseAIAnalysis } from '../config/planLimits'
import type { Campaign } from './Dashboard'
import './Editor.css'

interface EditorProps {
  campaign: Campaign
  onSave: (campaign: Campaign) => void
  onClose: () => void
  onUpgrade?: () => void
}

interface SmartBrief {
  campaignGoal: string
  customGoal?: string
  targetAudience: string
  keyMessage: string
  desiredAction: string
  description: string
}

interface MLSuggestions {
  suggestedTrigger?: string; triggerReasoning?: string; triggerLift?: number;
  suggestedTone?: string; toneReasoning?: string; toneLift?: number;
  suggestedCTA?: string; ctaReasoning?: string; ctaLift?: number;
  predictedObjection?: string; objectionLikelihood?: number;
  suggestedReframe?: string; dataSource: string;
}

interface EmailBrainContext {
  psychologicalTriggers: string[]
  communicationPattern: { tone: string; languageStyle: string; messagingFocus: string }
  predictedObjections: Array<{ objection: string; reframe: string; likelihood: number }>
  systemPrompt: string; currentStage: string; topPersona: string
  recommendations: { emailFrequency: string; contentFocus: string[]; toneSuggestion: string }
}

interface UploadedImage { url: string; publicId: string; filename: string; size: number; width?: number; height?: number }

interface ExtendedCampaign extends Campaign { nicheId?: string; industry?: string; company?: string }

const API_URL = import.meta.env.VITE_API_URL || 'https://emailai-backend-658e.onrender.com/api';

export default function Editor({ campaign, onSave, onClose, onUpgrade }: EditorProps) {
  const { user } = useAuth()
  const [editCampaign, setEditCampaign] = useState<ExtendedCampaign>(campaign as ExtendedCampaign)
  const [selectedTemplate, setSelectedTemplate] = useState<typeof TEMPLATES[0] | null>(null)
  const [aiGeneratedContent, setAiGeneratedContent] = useState<string>('')
  const [aiAnalysisUsed, setAIAnalysisUsed] = useState(0)
  const [emailBrainContext, setEmailBrainContext] = useState<EmailBrainContext | null>(null)
  const [viewMode, setViewMode] = useState<'visual' | 'html'>('visual')
  const [generating, setGenerating] = useState(false)
  const [saving, setSaving] = useState(false)
  const [sending, setSending] = useState(false)
  const [analyzing, setAnalyzing] = useState(false)
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null)
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [mlSuggestions, setMlSuggestions] = useState<MLSuggestions | null>(null)
  const [uploadedImages, setUploadedImages] = useState<UploadedImage[]>([])
  const [uploading, setUploading] = useState(false)
  const [showImageGallery, setShowImageGallery] = useState(false)
  const contentRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const userPlan = (user as any)?.selectedPlan || (user as any)?.plan || 'starter'

  const [brief, setBrief] = useState<SmartBrief>({
    campaignGoal: 'lead_generation', targetAudience: '', keyMessage: '',
    desiredAction: 'Schedule a demo', description: campaign.content || ''
  })

  // ✅ FIXED: Removed duplicate /api
  useEffect(() => {
    const fetchUsage = async () => {
      try {
        const token = localStorage.getItem('token') || localStorage.getItem('accessToken')
        const response = await fetch(`${API_URL}/ai-analysis/usage`, { headers: { 'Authorization': `Bearer ${token}` } })
        if (response.ok) { const data = await response.json(); setAIAnalysisUsed(data.used || 0); }
      } catch (error) { console.error('Failed to fetch AI usage:', error) }
    }
    fetchUsage()
  }, [])

  const showNotification = useCallback((type: 'success' | 'error', message: string) => {
    setNotification({ type, message }); setTimeout(() => setNotification(null), 4000)
  }, [])

  // ✅ FIXED: Removed duplicate /api
  const handleAnalyzeBrief = async () => {
    if (!brief.targetAudience || !brief.keyMessage || !brief.description) { showNotification('error', 'Please fill in all brief fields'); return }
    if (!canUseAIAnalysis(userPlan, aiAnalysisUsed)) { onUpgrade?.(); return }
    setAnalyzing(true)
    try {
      const token = localStorage.getItem('accessToken')
      const response = await fetch(`${API_URL}/emailbrain/analyze-brief`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ brief, nicheId: editCampaign.nicheId || "SAAS_B2B_01", industry: editCampaign.industry || "Technology", companyName: editCampaign.name || (user as any)?.company || "Your Company" })
      })
      if (!response.ok) { if (response.status === 429) { onUpgrade?.(); throw new Error('Analysis limit reached') } throw new Error('Failed to analyze brief') }
      const data = await response.json()
      if (data.emailBrainContext) setEmailBrainContext(data.emailBrainContext)
      if (data.suggestions) { setMlSuggestions(data.suggestions); setShowSuggestions(true) }
      setAIAnalysisUsed(prev => prev + 1)
      showNotification('success', 'AI analysis complete!')
    } catch (error) { showNotification('error', error instanceof Error ? error.message : 'Analysis failed') }
    finally { setAnalyzing(false) }
  }

  // ✅ FIXED: Removed duplicate /api
  const handleGenerateEmail = async () => {
    if (!editCampaign.subject) { showNotification('error', 'Please enter an email subject first'); return }
    if (!brief.targetAudience || !brief.keyMessage) { showNotification('error', 'Please complete the campaign brief first'); return }
    setGenerating(true)
    try {
      const token = localStorage.getItem('accessToken')
      const response = await fetch(`${API_URL}/email/generate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          subject: editCampaign.subject, campaignName: editCampaign.name,
          brief: { goal: brief.campaignGoal, targetAudience: brief.targetAudience, keyMessage: brief.keyMessage, desiredAction: brief.desiredAction, description: brief.description },
          emailBrainContext, mlInsights: mlSuggestions, templateId: selectedTemplate?.id,
          nicheId: undefined, stage: emailBrainContext?.currentStage || "consideration", persona: emailBrainContext?.topPersona || "the_pragmatist",
          audience: { companyName: (user as any)?.company || editCampaign.name || 'Your Company', userRole: (user as any)?.jobTitle || 'Professional', companySize: (user as any)?.companySize || '1-10', industry: (user as any)?.industry || 'other' }
        })
      })
      if (!response.ok) { const error = await response.json(); throw new Error(error.message || 'Failed to generate content') }
      const data = await response.json()
      const generatedContent = data.draft || data.content || 'No content generated'
      setAiGeneratedContent(generatedContent)
      if (selectedTemplate) { setEditCampaign(prev => ({ ...prev, content: applyTemplateToContent(selectedTemplate, generatedContent) })) }
      else { setEditCampaign(prev => ({ ...prev, content: generatedContent })) }
      setViewMode('visual')
      showNotification('success', `Email generated successfully!`)
    } catch (error) { showNotification('error', error instanceof Error ? error.message : 'Failed to generate email') }
    finally { setGenerating(false) }
  }

  // ✅ FIXED: Removed duplicate /api
  const handleSendEmail = async () => {
    if (!editCampaign.content) { showNotification('error', 'No content to send'); return }
    const recipient = prompt('Enter recipient email address:')
    if (!recipient) return
    setSending(true)
    try {
      const token = localStorage.getItem('accessToken')
      const res = await fetch(`${API_URL}/email/send`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ to: recipient, subject: editCampaign.subject, html: editCampaign.content, campaignId: editCampaign.id })
      })
      const data = await res.json()
      if (data.success) { showNotification('success', `Email sent to ${recipient}!`) }
      else { showNotification('error', data.message || 'Failed to send') }
    } catch (err: any) { showNotification('error', err.message) }
    finally { setSending(false) }
  }

  const applyTemplateToContent = (template: typeof TEMPLATES[0], content: string): string => {
    let finalContent = template.html
    finalContent = finalContent.replace(/{{subject}}/g, editCampaign.subject).replace(/{{date}}/g, new Date().toLocaleDateString()).replace(/{{company}}/g, editCampaign.name || 'Your Company').replace(/{{name}}/g, 'Valued Customer').replace(/{{title}}/g, editCampaign.subject).replace(/{{ctaText}}/g, brief.desiredAction || 'Get Started').replace(/{{content}}/g, content).replace(/{{[^}]+}}/g, '')
    return finalContent
  }

  const handleSave = async () => {
    if (!editCampaign.name) { showNotification('error', 'Please enter a campaign name'); return }
    setSaving(true)
    try { await onSave(editCampaign as Campaign); showNotification('success', 'Campaign saved successfully!') }
    catch (error) { showNotification('error', 'Failed to save campaign') }
    finally { setSaving(false) }
  }

  const canGenerate = Boolean(editCampaign.subject && brief.targetAudience && brief.keyMessage && brief.description)

  return (  


    <div className="editor-container">
      {notification && (<div className={`notification-toast ${notification.type}`}>{notification.type === 'success' ? <Check size={16} /> : <AlertCircle size={16} />}<span>{notification.message}</span></div>)}
      <main className="editor-main"><div className="editor-card">
        <div className="editor-header"><div><h1 className="editor-title">{campaign.id?.startsWith('camp_') ? '✨ Create Campaign' : '✏️ Edit Campaign'}</h1>{emailBrainContext && <p className="editor-subtitle">Stage: {emailBrainContext.currentStage} • Persona: {emailBrainContext.topPersona}</p>}</div><button onClick={onClose} className="close-btn"><X size={20} /></button></div>
        <div className="campaign-basics">
          <div className="form-group"><label>Campaign Name</label><input type="text" value={editCampaign.name} onChange={(e) => setEditCampaign(prev => ({ ...prev, name: e.target.value }))} placeholder="e.g., March Newsletter" className="form-input" /></div>
          <div className="form-group"><label>Email Subject</label><div className="subject-input-group"><input type="text" value={editCampaign.subject} onChange={(e) => setEditCampaign(prev => ({ ...prev, subject: e.target.value }))} placeholder="Enter email subject line" className="form-input" /><button onClick={handleGenerateEmail} disabled={generating || !canGenerate} className="generate-btn">{generating ? <><Loader size={16} className="spinner" /> Generating...</> : <><Zap size={16} /> Generate Email</>}</button></div>{!canGenerate && <p className="hint-text">⚡ Complete the brief below and add a subject to enable AI generation</p>}</div>
        </div>
        <AIAnalyzer brief={brief} onBriefChange={setBrief} campaignData={editCampaign} onAnalyze={handleAnalyzeBrief} onApplySuggestions={(s: any) => { if (s.suggestedTrigger) setBrief(prev => ({ ...prev, keyMessage: s.suggestedTrigger! })); if (s.suggestedCTA) setBrief(prev => ({ ...prev, desiredAction: s.suggestedCTA! })); setShowSuggestions(false); showNotification('success', 'AI suggestions applied!') }} mlSuggestions={mlSuggestions} showSuggestions={showSuggestions} onCloseSuggestions={() => setShowSuggestions(false)} analyzing={analyzing} aiAnalysisUsed={aiAnalysisUsed} onUpgrade={onUpgrade} />
        {emailBrainContext && (<div className="emailbrain-context"><h4>🧠 AI Insights</h4><div className="context-grid"><div className="context-item"><strong>Triggers:</strong> {emailBrainContext.psychologicalTriggers.slice(0, 3).join(', ')}</div><div className="context-item"><strong>Tone:</strong> {emailBrainContext.communicationPattern.tone}</div><div className="context-item"><strong>Focus:</strong> {emailBrainContext.recommendations.contentFocus.join(', ')}</div></div></div>)}
        <TemplateSelector onSelectTemplate={(t) => { setSelectedTemplate(t); if (aiGeneratedContent || editCampaign.content) { setEditCampaign(prev => ({ ...prev, content: applyTemplateToContent(t, aiGeneratedContent || editCampaign.content) })); showNotification('success', `${t.name} template applied!`) } else { setEditCampaign(prev => ({ ...prev, content: t.html })); setViewMode('html'); showNotification('success', `${t.name} template selected. Generate AI content to fill it.`) } }} selectedTemplateId={selectedTemplate?.id} onUpgrade={onUpgrade} />
        <div className="content-section"><div className="content-header"><label>Email Content</label><div className="content-controls"><div className="view-toggle"><button onClick={() => setViewMode('visual')} className={`toggle-btn ${viewMode === 'visual' ? 'active' : ''}`}><Eye size={14} /> Visual</button><button onClick={() => setViewMode('html')} className={`toggle-btn ${viewMode === 'html' ? 'active' : ''}`}><Code size={14} /> HTML</button></div><button onClick={() => { if (!editCampaign.content) { showNotification('error', 'No content to preview'); return } const pw = window.open('', '_blank'); if (pw) { pw.document.write(`<!DOCTYPE html><html><head><title>Preview</title></head><body><div style="max-width:600px;margin:0 auto;padding:20px">${editCampaign.content}</div></body></html>`); pw.document.close() } }} className="preview-btn">👁️ Preview</button></div></div>{viewMode === 'visual' ? <div ref={contentRef} className="content-preview"
         dangerouslySetInnerHTML={{ __html: editCampaign.content || '<p class="placeholder">Generate AI content or write your email here...</p>' }} /> : <textarea value={editCampaign.content} onChange={(e) => setEditCampaign(prev => ({ ...prev, content: e.target.value }))} rows={18} className="content-textarea" placeholder="<p>Your HTML content here...</p>" />}<p className="content-length">{editCampaign.content?.length || 0} characters</p></div>
        <div className="editor-actions">
  <button onClick={onClose} className="cancel-btn">Cancel</button>
  <button onClick={handleSendEmail} disabled={sending || !editCampaign.content} style={{ padding: '12px 24px', background: '#10b981', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px', marginRight: '12px' }}>
    {sending ? <><Loader size={16} className="spinner" /> Sending...</> : <><Send size={16} /> Send Email</>}
  </button>
  <button onClick={handleSave} disabled={saving || !editCampaign.name} className="save-btn">
  {saving ? <><Loader size={16} className="spinner" /> Saving...</> : <><Save size={16} /> Save Campaign</>}
</button>
</div>
      </div></main>
    </div>
  )
}